import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import GlassCard from './GlassCard';
import FuturisticButton from './FuturisticButton';
import { useApiKey } from '../contexts/ApiKeyContext';

const {
  FiCheck,
  FiExternalLink,
  FiEye,
  FiEyeOff,
  FiKey,
  FiTrash2,
  FiX
} = FiIcons;

const ApiKeyModal = ({ isOpen, onClose }) => {
  const { apiKey, updateApiKey, clearApiKey } = useApiKey();
  const [keyInput, setKeyInput] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return undefined;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (event) => {
    event.preventDefault();
    const key = keyInput.trim();

    if (!key.startsWith('sk-') || key.length <= 20) {
      setError('Enter a valid OpenAI API key beginning with sk-.');
      return;
    }

    updateApiKey(key);
    setKeyInput('');
    setError('');
    onClose();
  };

  return createPortal(
    <AnimatePresence>
      <div
        className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm overflow-y-auto"
        role="presentation"
      >
        <div className="min-h-full flex items-start sm:items-center justify-center p-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="api-key-title"
            initial={{ opacity: 0, y: -16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -16, scale: 0.98 }}
            className="w-full max-w-md my-auto"
          >
            <GlassCard className="p-4 sm:p-6" hover={false}>
              <div className="flex justify-between items-center mb-5">
                <h2 id="api-key-title" className="text-xl sm:text-2xl font-bold text-white terminal-text">
                  OpenAI API Key
                </h2>
                <button
                  onClick={onClose}
                  className="p-2 rounded-lg text-green-400 hover:bg-white/10"
                  aria-label="Close API key dialog"
                >
                  <SafeIcon icon={FiX} className="h-5 w-5" />
                </button>
              </div>

              {apiKey && (
                <GlassCard className="p-4 mb-4 border border-green-400/30" hover={false}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center min-w-0">
                      <SafeIcon icon={FiCheck} className="h-5 w-5 text-green-400 mr-2" />
                      <div>
                        <p className="font-semibold text-green-400 text-sm">Key available</p>
                        <p className="text-xs text-green-200">
                          {apiKey.slice(0, 7)}…{apiKey.slice(-4)}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={clearApiKey}
                      className="p-2 text-red-300 hover:bg-red-500/10 rounded-lg"
                      aria-label="Clear API key"
                    >
                      <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                    </button>
                  </div>
                </GlassCard>
              )}

              <GlassCard className="p-4 mb-4 border border-yellow-400/30" hover={false}>
                <SafeIcon icon={FiKey} className="h-5 w-5 text-yellow-400 mb-2" />
                <h3 className="font-semibold text-white mb-2">Privacy and security</h3>
                <p className="text-sm text-green-100">
                  The key is kept in this browser tab session and sent directly to OpenAI.
                  It is removed when the tab session ends or when you clear it. Avoid using
                  this feature on a shared or untrusted device.
                </p>
              </GlassCard>

              <form onSubmit={handleSubmit}>
                <label htmlFor="openai-key" className="block text-sm font-medium text-white mb-2">
                  {apiKey ? 'Replace API key' : 'Enter API key'}
                </label>
                <div className="relative">
                  <input
                    id="openai-key"
                    type={showKey ? 'text' : 'password'}
                    value={keyInput}
                    onChange={(event) => {
                      setKeyInput(event.target.value);
                      setError('');
                    }}
                    placeholder="sk-..."
                    autoComplete="off"
                    className="w-full px-4 py-3 pr-12 bg-black/60 border border-green-400/30 rounded-lg text-white focus:ring-2 focus:ring-green-400"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowKey((value) => !value)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-green-400"
                    aria-label={showKey ? 'Hide API key' : 'Show API key'}
                  >
                    <SafeIcon icon={showKey ? FiEyeOff : FiEye} className="h-5 w-5" />
                  </button>
                </div>

                {error && <p className="text-sm text-red-300 mt-2">{error}</p>}

                <p className="text-xs text-green-200 mt-3">
                  Medical-report text is transmitted to OpenAI for processing and is
                  subject to your OpenAI account terms and data controls.
                </p>

                <a
                  href="https://platform.openai.com/api-keys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-green-400 hover:text-green-300 inline-flex items-center mt-3"
                >
                  Open OpenAI API keys
                  <SafeIcon icon={FiExternalLink} className="h-3 w-3 ml-1" />
                </a>

                <FuturisticButton type="submit" size="lg" className="w-full mt-5">
                  {apiKey ? 'Replace Key' : 'Use Key for This Session'}
                </FuturisticButton>
              </form>
            </GlassCard>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>,
    document.body
  );
};

export default ApiKeyModal;